import * as THREE from 'three';
import { TWO_PI } from './kepler';

/**
 * Soft textures drawn on a canvas, and the two small point shaders the chapter uses.
 * Every texture is white with the shape in its alpha, so a material colour above 1.0 tints it and blooms.
 */

function canvasTexture(size: number, draw: (g: CanvasRenderingContext2D, c: number) => void): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const g = canvas.getContext('2d')!;
  draw(g, size / 2);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** A soft round glow: bright core and a smooth, gaussian-like fall-off that reaches zero well inside the quad. */
export function glowTexture(size = 128): THREE.CanvasTexture {
  return canvasTexture(size, (g, c) => {
    const grad = g.createRadialGradient(c, c, 0, c, c, c);
    const n = 24;
    for (let i = 0; i <= n; i++) {
      const r = i / n;
      const fade = Math.pow(1 - r, 2); // pull the tail to exactly zero at the rim
      const a = (Math.exp(-Math.pow(r / 0.26, 2)) + 0.12 * Math.exp(-Math.pow(r / 0.55, 2))) * fade;
      grad.addColorStop(r, `rgba(255,255,255,${Math.min(1, a).toFixed(4)})`);
    }
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
  });
}

/** The warm haze around the hole. Empty in the middle so the black disc stays pure black. */
export function haloTexture(size = 256): THREE.CanvasTexture {
  return canvasTexture(size, (g, c) => {
    const grad = g.createRadialGradient(c, c, 0, c, c, c);
    grad.addColorStop(0, 'rgba(255,255,255,0)');
    grad.addColorStop(0.17, 'rgba(255,255,255,0)');
    grad.addColorStop(0.24, 'rgba(255,255,255,0.9)');
    grad.addColorStop(0.36, 'rgba(255,255,255,0.45)');
    grad.addColorStop(0.6, 'rgba(255,255,255,0.12)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
  });
}

export interface RingOptions {
  size?: number;
  /** ring radius as a fraction of the half-size */
  radius?: number;
  /** stroke width in pixels at the default size */
  width?: number;
  /** [dash, gap] in pixels: a dashed ring with no halo */
  dash?: [number, number];
}

/** A thin ring with a faint halo, or a dashed one. */
export function ringTexture(o: RingOptions = {}): THREE.CanvasTexture {
  const size = o.size ?? 256, radius = o.radius ?? 0.8, width = o.width ?? 5;
  return canvasTexture(size, (g, c) => {
    g.translate(c, c);
    const r = c * radius;
    if (!o.dash) {
      for (let i = 4; i >= 1; i--) {
        g.lineWidth = width * (1 + i * 1.8);
        g.strokeStyle = `rgba(255,255,255,${0.07 / (1 + (i - 1) * 0.5)})`;
        g.beginPath(); g.arc(0, 0, r, 0, TWO_PI); g.stroke();
      }
    } else {
      g.setLineDash(o.dash);
    }
    g.lineWidth = width;
    g.lineCap = 'butt';
    g.strokeStyle = 'rgba(255,255,255,1)';
    g.beginPath(); g.arc(0, 0, r, 0, TWO_PI); g.stroke();
  });
}

/** The player's mark: a ring, a dot and four ticks. */
export function reticleTexture(size = 128): THREE.CanvasTexture {
  return canvasTexture(size, (g, c) => {
    g.translate(c, c);
    g.strokeStyle = 'rgba(255,255,255,1)';
    g.fillStyle = 'rgba(255,255,255,1)';
    g.lineWidth = size * 0.028;
    g.beginPath(); g.arc(0, 0, c * 0.46, 0, TWO_PI); g.stroke();
    g.beginPath(); g.arc(0, 0, c * 0.07, 0, TWO_PI); g.fill();
    for (let i = 0; i < 4; i++) {
      g.save(); g.rotate((i * Math.PI) / 2);
      g.beginPath(); g.moveTo(c * 0.62, 0); g.lineTo(c * 0.9, 0); g.stroke();
      g.restore();
    }
  });
}

/* ---- point shaders ---- */

const STAR_VERT = /* glsl */ `
attribute vec3 aColor;
attribute float aSize;
uniform float uScale;
varying vec3 vColor;
void main() {
  vColor = aColor;
  gl_PointSize = aSize * uScale;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const STAR_FRAG = /* glsl */ `
uniform sampler2D map;
varying vec3 vColor;
void main() {
  float a = texture2D(map, gl_PointCoord).a;
  gl_FragColor = vec4(vColor, a);
}`;

/**
 * Pixel-sized additive points with a colour and a size per point. Used for the star field and the S-stars.
 * `uScale` is the render height over 1080, so sizes hold across resolutions.
 */
export function makeStarMaterial(map: THREE.Texture): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { map: { value: map }, uScale: { value: 1 } },
    vertexShader: STAR_VERT,
    fragmentShader: STAR_FRAG,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });
}

const TRAIL_VERT = /* glsl */ `
attribute float aSlot;
uniform float uHead;
uniform float uCount;
uniform float uCap;
uniform float uPx;
uniform float uSize;
varying float vAlpha;
void main() {
  float k = mod(uHead - aSlot + uCap, uCap);          // how many points older than the newest
  float used = step(k + 0.5, uCount);
  float t = k / (uCap - 1.0);                          // 0 at the star, 1 at the tail
  vAlpha = used * pow(1.0 - t, 1.8);
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = uSize * mix(1.0, 0.45, t) * uPx / max(-mv.z, 0.1);
  gl_Position = projectionMatrix * mv;
}`;

const TRAIL_FRAG = /* glsl */ `
uniform sampler2D map;
uniform vec3 uColor;
varying float vAlpha;
void main() {
  float a = texture2D(map, gl_PointCoord).a * vAlpha;
  gl_FragColor = vec4(uColor, a);
}`;

/**
 * The star's trail: world-sized additive points that fade and shrink toward the tail.
 * Age is worked out in the shader from the ring-buffer head, so a frame only uploads positions.
 * `uPx` is the number of pixels one world unit covers at distance 1.
 */
export function makeTrailMaterial(map: THREE.Texture, capacity: number, color: THREE.Color, size: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      map: { value: map },
      uHead: { value: 0 },
      uCount: { value: 0 },
      uCap: { value: capacity },
      uPx: { value: 1000 },
      uSize: { value: size },
      uColor: { value: color }
    },
    vertexShader: TRAIL_VERT,
    fragmentShader: TRAIL_FRAG,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });
}
